import { MapContainer, TileLayer, Marker, Popup, ZoomControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";

const DEPT_CENTER = [48.42, 1.35];
const DEFAULT_ZOOM = 9;

// Marqueur commune : pictogramme si 1 club, badge compteur si plusieurs.
function communeIcon(count) {
  const inner = count > 1
    ? `<span style="font-weight:800;font-size:14px;font-family:Outfit,sans-serif">${count}</span>`
    : `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
         <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
         <circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
         <path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;
  const size = count > 1 ? 36 : 30;
  const html = `
    <div style="position:relative">
      <div style="width:${size}px;height:${size}px;border-radius:50%;background:#059669;
                  box-shadow:0 4px 10px rgba(5,150,105,.45);display:flex;align-items:center;
                  justify-content:center;color:#fff;border:3px solid #fff">${inner}</div>
      <div style="position:absolute;left:50%;top:${size}px;transform:translateX(-50%);
                  width:0;height:0;border:6px solid transparent;border-top-color:#059669"></div>
    </div>`;
  return L.divIcon({ html, className: "", iconSize: [size, size + 10], iconAnchor: [size / 2, size + 10] });
}

// Regroupe les associations par commune (clé = nom de ville normalisé).
function groupByCommune(points) {
  const groups = {};
  points.forEach((p) => {
    const key = (p.city || "—").trim().toLowerCase();
    if (!groups[key]) groups[key] = { key, city: p.city || "Eure-et-Loir", lat: p.lat, lng: p.lng, clubs: [] };
    groups[key].clubs.push(p);
  });
  return Object.values(groups);
}

export default function AssociationsMap({ points = [], onCommuneSelect }) {
  const navigate = useNavigate();
  const communes = useMemo(
    () => groupByCommune((points || []).filter((p) => typeof p.lat === "number" && typeof p.lng === "number")),
    [points]
  );
  const total = useMemo(() => communes.reduce((n, c) => n + c.clubs.length, 0), [communes]);

  return (
    <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white"
         data-testid="associations-leaflet-map">
      <MapContainer center={DEPT_CENTER} zoom={DEFAULT_ZOOM} scrollWheelZoom={false}
                    zoomControl={false} style={{ height: "440px", width: "100%" }}>
        <ZoomControl position="bottomright" />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {communes.map((c) => (
          <Marker key={c.key} position={[c.lat, c.lng]} icon={communeIcon(c.clubs.length)}
                  data-testid={`assoc-map-commune-${c.key}`}
                  eventHandlers={{ click: (e) => {
                    const m = e?.target?._map;
                    if (m) m.flyTo([c.lat, c.lng], 12, { duration: 0.8 });
                    onCommuneSelect?.(c.city);
                  } }}>
            <Popup>
              <div style={{ fontFamily: "Outfit, sans-serif", minWidth: 190, maxWidth: 240 }}>
                <div style={{ fontWeight: 800, marginBottom: 2 }}>{c.city}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
                  {c.clubs.length} association{c.clubs.length > 1 ? "s" : ""}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 2,
                              maxHeight: 180, overflowY: "auto" }}>
                  {c.clubs.map((club) => (
                    <button
                      key={club.id}
                      onClick={() => navigate(`/associations/${club.slug}`)}
                      data-testid={`assoc-map-link-${club.id}`}
                      style={{ display: "flex", flexDirection: "column", alignItems: "flex-start",
                               textAlign: "left", padding: "6px 8px", borderRadius: 8, border: "none",
                               background: "#f8fafc", cursor: "pointer", width: "100%" }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>{club.name}</span>
                      <span style={{ fontSize: 11, color: "#059669", fontWeight: 600 }}>
                        {club.category_label} · Voir la fiche →
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-3 py-2 rounded-xl border border-slate-200 shadow-sm text-xs text-slate-600 z-[500]"
           data-testid="associations-map-legend">
        <div className="font-bold text-slate-900 mb-1">
          {total} association{total > 1 ? "s" : ""} · {communes.length} commune{communes.length > 1 ? "s" : ""}
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-emerald-600"></span> Clubs par commune
        </div>
      </div>
    </div>
  );
}
